import { cva } from 'class-variance-authority';

import { focusRingClasses, statusBorderVariants, statusTextVariants } from '@/lib/shared-variants';

/**
 * Notice container styles
 * White card with a 4px left status stripe and a small drop shadow.
 * `border-l-4` (4px) is used directly because the `md` border-width token is 12px
 * in code while Figma's `border.width.md` is 4px.
 */
export const noticeVariants = cva(
  ['relative', 'flex', 'w-full', 'items-center', 'bg-surface', 'border-l-4', 'shadow-sm'],
  {
    variants: {
      kind: statusBorderVariants,
      scale: {
        s: 'py-px',
        m: '',
        l: '',
      },
    },
    defaultVariants: {
      kind: 'brand',
      scale: 'm',
    },
  }
);

/**
 * Notice leading status icon slot
 */
export const noticeIconVariants = cva(['flex', 'shrink-0', 'self-stretch', 'items-center'], {
  variants: {
    kind: statusTextVariants,
    scale: {
      s: 'pl-md py-sm [&_svg]:size-4',
      m: 'pl-lg py-md [&_svg]:size-4',
      l: 'pl-xl py-lg [&_svg]:size-6',
    },
  },
  defaultVariants: {
    kind: 'brand',
    scale: 'm',
  },
});

/**
 * Notice text column (title, message, link, children)
 */
export const noticeContentVariants = cva(['flex', 'min-w-0', 'flex-1', 'flex-col', 'items-start'], {
  variants: {
    scale: {
      s: 'pl-md pr-sm py-sm',
      m: 'pl-lg pr-md py-md',
      l: 'pl-xl pr-lg py-lg',
    },
  },
  defaultVariants: {
    scale: 'm',
  },
});

/**
 * Notice title styles
 */
export const noticeTitleVariants = cva(
  ['w-full', 'py-xxxs', 'font-bold', 'text-foreground', '[word-break:break-word]'],
  {
    variants: {
      scale: {
        s: 'text-size-n1 leading-[22px]',
        m: 'text-size-0 leading-6',
        l: 'text-size-1 leading-[26px]',
      },
    },
    defaultVariants: {
      scale: 'm',
    },
  }
);

/**
 * Notice message styles
 */
export const noticeDescriptionVariants = cva(
  ['w-full', 'py-xxxs', 'font-normal', 'text-foreground-secondary', '[word-break:break-word]'],
  {
    variants: {
      scale: {
        s: 'pr-sm text-size-n2 leading-[18px]',
        m: 'pr-md text-size-n1 leading-[22px]',
        l: 'pr-lg text-size-0 leading-6',
      },
    },
    defaultVariants: {
      scale: 'm',
    },
  }
);

/**
 * Notice link styles — link colour; the underline is `noticeLinkIndicatorVariants`
 */
export const noticeLinkVariants = cva(
  [
    'group',
    'relative',
    'inline-flex',
    'cursor-pointer',
    'items-center',
    'py-xxxs',
    'text-foreground-link',
    'rounded-xs',
    focusRingClasses,
  ],
  {
    variants: {
      scale: {
        s: 'text-size-n2 leading-[18px]',
        m: 'text-size-n1 leading-[22px]',
        l: 'text-size-0 leading-6',
      },
    },
    defaultVariants: {
      scale: 'm',
    },
  }
);

/**
 * Notice link underline — a 1px bar at 40% opacity, full opacity on hover
 */
export const noticeLinkIndicatorVariants = cva([
  'absolute',
  'inset-x-0',
  'bottom-0',
  'h-px',
  'bg-foreground-link',
  'opacity-light',
  'transition-opacity',
  'group-hover:opacity-full',
]);

/**
 * Notice trailing actions slot
 */
export const noticeActionsVariants = cva(['flex', 'shrink-0', 'self-stretch', 'items-center'], {
  variants: {
    scale: {
      s: 'gap-xxs',
      m: 'gap-sm',
      l: 'gap-sm',
    },
  },
  defaultVariants: {
    scale: 'm',
  },
});

/**
 * Notice close (x) button
 */
export const noticeCloseVariants = cva(
  [
    'flex',
    'shrink-0',
    'self-stretch',
    'items-center',
    'justify-center',
    'text-foreground-muted',
    'transition-colors',
    'hover:text-foreground',
    'focus-visible:outline-none',
    'focus-visible:ring-2',
    'focus-visible:ring-inset',
    'focus-visible:ring-focus',
  ],
  {
    variants: {
      scale: {
        s: 'px-sm py-md [&_svg]:size-4',
        m: 'px-lg py-md [&_svg]:size-4',
        l: 'p-xl [&_svg]:size-6',
      },
    },
    defaultVariants: {
      scale: 'm',
    },
  }
);
